import { keepPreviousData, useQuery } from "@tanstack/react-query"

import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { KPIRequest } from "@/types/kpiTypes.ts"
import { numberFmt } from "@/util/numberFmt.ts"

import { getKPISummaryTable } from "../api/getKPISummaryTable.ts"

export default function KPISummaryTable({ asOf, kpis }: { asOf: Date; kpis: KPIRequest[] }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["incentiveKPISummaryTable", { asOf, kpis }],
    queryFn: () => getKPISummaryTable(asOf, kpis),
    placeholderData: keepPreviousData,
    refetchInterval: 10 * 60 * 1000,
  })
  if (isPending)
    return <Skeleton className="w-full h-full" style={{ height: `${kpis.length * 53 + 52.5}px` }} />
  if (isError)
    return (
      <div
        className="w-full border p-4 shadow-sm rounded-md h-full"
        style={{ height: `${kpis.length * 53}px` }}
      >
        <span className="text-status-bad font-semibold">Network Error</span>
      </div>
    )

  return (
    <div className="border rounded-md shadow-sm max-w-full overflow-x-hidden h-full">
      <Table>
        <TableHeader className="bg-card-header">
          <TableRow>
            <TableHead className="p-4 text-center">MTD</TableHead>
            <TableHead className="p-4 text-center">LM</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.monthToDate.map((kpi, i) => {
            const mtd =
              kpi.values?.find((v) => v.type == "Actual" || v.type == "Mixed")?.value ||
              kpi.values?.at(0)?.value
            const lm = data.lastMonth.at(i)?.values?.find((v) => v.type == "Actual")?.value
            return (
              <TableRow key={kpi.name}>
                <TableCell
                  className={`p-4 text-center ${
                    mtd === undefined && "text-muted-foreground italic"
                  }`}
                  key={`${kpi.name}-mtd`}
                >
                  {mtd != undefined ? `${numberFmt(mtd)}${kpi.unit == "%" ? "%" : ""}` : "N/A"}
                </TableCell>
                <TableCell
                  className={`p-4 text-center ${
                    lm === undefined && "text-muted-foreground italic"
                  }`}
                  key={`${kpi.name}-lm`}
                >
                  {lm != undefined ? `${numberFmt(lm)}${kpi.unit == "%" ? "%" : ""}` : "N/A"}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
